import { useEffect, useRef, useState, type FC } from 'react';
import { toast } from 'react-toastify';
import { motion, type HTMLMotionProps } from 'motion/react';
import { BaseModal } from 'src/components/AriaModal';
import { AriaButton } from 'src/components/Button';
import SegmentedProgressBar from 'src/components/SegmentedProgressBar';
import { useFacebook } from 'src/utils/useFacebook';
import { useI18nContext } from 'src/i18n/i18n-react';
import { Icon } from 'src/components/Icon';
import MetaLogo from 'src/assets/svgicons/brands/meta.svg';
import ArrowRight from 'src/assets/svgicons/line/arrow-right.svg';
import InfoCircle from 'src/assets/svgicons/line/info-circle.svg';
import Tiktok from 'src/assets/svgicons/brands/TikTokColor.svg';
import Spinner from 'src/components/Loaders/Spinner';
import Alert from 'src/components/AlertV2/Alert';
import useNewAnalyticsEvent from 'src/utils/useNewAnalyticsEvent';
import { TikTokConnect } from 'src/components/TikTokConnect/TikTokConnect';
import { trpc } from 'src/lib/trpc';
import ComingSoonAdAccountPlatforms from 'src/components/ComingSoonAdAccountPlatforms/ComingSoonAdAccountPlatforms';
import { InsightsAdAccountSelectorList } from '../InsightsAdAccountSelectorList/InsightsAdAccountSelectorList';

type Props = {
  show: boolean;
  onClose: () => void;
  title?: string;
};

const InsightsOnboardingModal: FC<Props> = ({ show, onClose, title }) => {
  const [step, setStep] = useState(1);
  const direction = useRef<-1 | 0 | 1>(0);
  const [selectedAdAccountIds, setSelectedAdAccountIds] = useState<string[]>(
    []
  );
  const [selectedPlatform, setSelectedPlatform] = useState<
    'facebook' | 'tiktok'
  >('facebook');
  const [tikTokAuthState, setTikTokAuthState] = useState<'init' | 'login'>(
    'init'
  );
  const trpcUtils = trpc.useUtils();
  const { recordEvent } = useNewAnalyticsEvent();
  const { LL } = useI18nContext();
  const {
    authenticate: authenticateFacebook,
    authState: facebookAuthState,
    updateIntegration: updateFacebookIntegration,
  } = useFacebook();

  const tikTokAdvertiserAccounts =
    trpc.tikTok.getCachedAdvertiserAccounts.useQuery(undefined, {
      enabled: false,
    });
  const connectTikTokIntegration =
    trpc.tikTok.connectAdvertiserAccounts.useMutation({
      onSuccess: () => {
        void trpcUtils.integrations.getIntegrations.invalidate();
        void trpcUtils.insightsAccounts.getManyAdAccounts.invalidate();
        void trpcUtils.insightsAccounts.getAdAccount.invalidate();

        toast.success(LL.tikTok.oauth.exchange.success(), {
          className: 'toast-success',
          autoClose: 5000,
        });
      },
      onError: () => {
        toast.error(LL.tikTok.oauth.exchange.error.body1());
      },
    });

  useEffect(() => {
    if (step === 1 && facebookAuthState.status === 'success') {
      nextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facebookAuthState.status]);

  function nextStep() {
    direction.current = 1;
    setStep(step + 1);
  }

  function previousStep() {
    direction.current = -1;
    setStep(step - 1);
  }

  const stepsPercentages = (() => {
    const percentages = [10, 0];

    switch (step) {
      case 1: {
        switch (facebookAuthState.status) {
          case 'login':
            percentages[0] = 33;
            break;
          case 'pending':
            percentages[0] = 66;
            break;
          case 'success':
            percentages[0] = 100;
            break;
        }
        if (tikTokAuthState === 'login') {
          percentages[0] = 66;
        }
        break;
      }
      case 2: {
        percentages[0] = 100;
        percentages[1] = 10;
        if (selectedAdAccountIds.length > 0) {
          percentages[1] = 66;
        }
        if (
          updateFacebookIntegration.isLoading ||
          updateFacebookIntegration.isSuccess
        ) {
          percentages[1] = 100;
        }
        break;
      }
    }

    return percentages;
  })();

  useEffect(() => {
    if (
      step === 2 &&
      selectedPlatform === 'tiktok' &&
      !tikTokAdvertiserAccounts.data?.advertiserAccounts.length
    ) {
      toast.warn(LL.insights.onboarding.pleaseConnectTikTok());
      previousStep();
    }
    // omitting `previousStep` function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, tikTokAdvertiserAccounts.data, LL, step]);

  const stepAnimationProps: HTMLMotionProps<'div'> = {
    initial: { x: direction.current * 30, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    transition: {
      duration: 0.2,
      bounce: 0,
    },
  };

  const showLoader =
    facebookAuthState.status === 'login' ||
    facebookAuthState.status === 'pending' ||
    updateFacebookIntegration.isLoading ||
    tikTokAuthState === 'login';

  return (
    <BaseModal
      modalClassName="bg-white rounded-lg text-primary"
      dialogClassName="w-full sm:w-[478px] p-6 flex flex-col gap-6 relative"
      onClose={onClose}
      show={show}
    >
      <SegmentedProgressBar stepsPercentages={stepsPercentages} />
      {step === 1 && (
        <motion.div {...stepAnimationProps} className="space-y-6">
          <h1 className="text-pretty text-2xl font-semibold leading-8">
            {title ?? LL.insights.onboarding.step1Title()}
          </h1>
          <div className="space-y-3">
            <h2 className="text-sm font-semibold">
              {LL.insights.onboarding.selectPlatform()}
            </h2>
            <div className="space-y-2">
              <AriaButton
                variant="custom"
                className="group h-fit w-full justify-start rounded-lg border border-solid border-purple-200 !p-3 hover:bg-purple-100 disabled:opacity-50"
                onPress={() => {
                  setSelectedPlatform('facebook');
                  void authenticateFacebook();
                }}
              >
                <div className="rounded-lg border border-solid border-secondary/80 bg-white p-2">
                  <MetaLogo className="size-6" />
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-semibold">Meta</h3>
                  <p className="text-xs font-medium text-[#917DD2]">
                    {LL.insights.onboarding.connectMeta()}
                  </p>
                </div>
                <Icon className="ml-auto size-5 -translate-x-px opacity-50 transition-transform group-hover:translate-x-0 group-hover:opacity-100">
                  <ArrowRight />
                </Icon>
              </AriaButton>
              <TikTokConnect
                variant="custom"
                className="group h-fit w-full justify-start rounded-lg border border-solid border-purple-200 !p-3 hover:bg-purple-100 disabled:opacity-50"
                additionalOnPress={() => setTikTokAuthState('login')}
                onPopupClose={async () => {
                  await tikTokAdvertiserAccounts.refetch();
                  setSelectedPlatform('tiktok');
                  setTikTokAuthState('init');
                  nextStep();
                }}
                icon={null}
              >
                <div className="rounded-lg border border-solid border-secondary/80 bg-white p-2">
                  <Tiktok className="size-6" />
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-semibold">TikTok</h3>
                  <p className="text-xs font-medium text-[#917DD2]">
                    {LL.insights.onboarding.connectTikTok()}
                  </p>
                </div>
                <Icon className="ml-auto size-5 -translate-x-px opacity-50 transition-transform group-hover:translate-x-0 group-hover:opacity-100">
                  <ArrowRight />
                </Icon>
              </TikTokConnect>
              <ComingSoonAdAccountPlatforms />
            </div>
          </div>
          {facebookAuthState.status === 'error' && (
            <Alert
              className="w-full"
              title={LL.errors.genericTitle()}
              variant="danger"
            >
              {LL.errors.genericWithDetail({
                detail: facebookAuthState.error.message ?? 'Unknown error',
              })}
            </Alert>
          )}
          <div className="flex justify-between gap-2">
            <AriaButton variant="text" onPress={onClose}>
              {LL.cancel()}
            </AriaButton>
            <AriaButton
              onPress={nextStep}
              style={{
                visibility:
                  facebookAuthState.status === 'success' ||
                  (tikTokAdvertiserAccounts.data &&
                    tikTokAdvertiserAccounts.data.advertiserAccounts.length > 0)
                    ? 'visible'
                    : 'hidden',
              }}
            >
              {LL.continue()}
            </AriaButton>
          </div>
        </motion.div>
      )}

      {step === 2 && (
        <motion.div {...stepAnimationProps} className="space-y-6">
          <h1 className="text-pretty text-2xl font-semibold leading-8">
            {LL.insights.onboarding.step2Title()}
          </h1>
          {/* Facebook ad accounts list */}
          {selectedPlatform === 'facebook' &&
            facebookAuthState.status === 'success' && (
              <>
                <InsightsAdAccountSelectorList
                  label={LL.insights.accounts.accountSelect.title()}
                  advertiserAccounts={facebookAuthState.adAccounts}
                  selectedAdvertiserIds={selectedAdAccountIds}
                  setSelectedAdvertiserIds={setSelectedAdAccountIds}
                  platform="facebook"
                />
                <div className="flex gap-1 text-[#917DD2]">
                  <Icon className="size-4">
                    <InfoCircle />
                  </Icon>
                  <p className="text-xs">{LL.insights.onboarding.syncInfo()}</p>
                </div>
                <div className="flex justify-between gap-2">
                  <AriaButton variant="text" onPress={previousStep}>
                    {LL.back()}
                  </AriaButton>
                  <AriaButton
                    htmlType="submit"
                    onPress={() => {
                      const selectedAdAccounts =
                        facebookAuthState.adAccounts.filter((x) =>
                          selectedAdAccountIds.includes(x.accountId)
                        );

                      updateFacebookIntegration.mutate({
                        fbUserID: facebookAuthState.fbUserID,
                        accessToken: facebookAuthState.accessToken,
                        adAccounts: selectedAdAccounts,
                      });

                      void recordEvent({
                        action: 'Post-FB Auth - Accounts Connected',
                        target: 'Insights Account',
                        metadata: {
                          accounts: facebookAuthState.adAccounts
                            .map(
                              (adAccount) =>
                                `${adAccount.accountId}|${adAccount.name}`
                            )
                            .join(', '),
                        },
                      });
                    }}
                    isDisabled={selectedAdAccountIds.length === 0}
                    loading={updateFacebookIntegration.isLoading}
                  >
                    {LL.confirmSelection()}
                  </AriaButton>
                </div>
              </>
            )}

          {/* TikTok ad accounts list */}
          {selectedPlatform === 'tiktok' && tikTokAdvertiserAccounts.data && (
            <>
              <InsightsAdAccountSelectorList
                label={LL.insights.accounts.accountSelect.title()}
                advertiserAccounts={tikTokAdvertiserAccounts.data.advertiserAccounts.map(
                  (advertiser) => ({
                    advertiserId: advertiser.advertiser_id,
                    name: advertiser.name,
                  })
                )}
                selectedAdvertiserIds={selectedAdAccountIds}
                setSelectedAdvertiserIds={setSelectedAdAccountIds}
                platform="tiktok"
              />
              <div className="flex gap-1 text-[#917DD2]">
                <Icon className="size-4">
                  <InfoCircle />
                </Icon>
                <p className="text-xs">{LL.insights.onboarding.syncInfo()}</p>
              </div>
              <div className="flex justify-between gap-2">
                <AriaButton variant="text" onPress={previousStep}>
                  {LL.back()}
                </AriaButton>
                <AriaButton
                  htmlType="submit"
                  onPress={() => {
                    const selectedAdAccounts =
                      tikTokAdvertiserAccounts.data.advertiserAccounts.filter(
                        (a) => selectedAdAccountIds.includes(a.advertiser_id)
                      );

                    connectTikTokIntegration.mutate({
                      advertiserIds: selectedAdAccounts.map(
                        (a) => a.advertiser_id
                      ),
                    });

                    void recordEvent({
                      action: 'Post-TT Auth - Accounts Connected',
                      target: 'Insights Account',
                      metadata: {
                        accounts: selectedAdAccounts.map((a) => a.name),
                      },
                    });
                  }}
                  isDisabled={selectedAdAccountIds.length === 0}
                  loading={connectTikTokIntegration.isLoading}
                >
                  {LL.confirmSelection()}
                </AriaButton>
              </div>
            </>
          )}
        </motion.div>
      )}
      {/* Loader */}
      {showLoader && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-lg bg-white/60 text-sm font-semibold backdrop-blur-[2px]">
          <Spinner className="size-6" />
          {facebookAuthState.status === 'pending' && (
            <p>{LL.insights.onboarding.connectingAccount()}</p>
          )}
          {updateFacebookIntegration.isLoading && (
            <p>{LL.insights.onboarding.creatingCustomDashboard()}</p>
          )}
        </div>
      )}
    </BaseModal>
  );
};

export default InsightsOnboardingModal;
